import { Suspense, type ReactNode } from 'react';
import { HeartIcon } from './assets/heart-icon';
import { DonationsIcon } from './assets/donations-icon';
import { CollectedIcon } from './assets/collected-icon';
import { EmptyChar } from './empty-char';
import { useLingui } from '@lingui/react/macro';
import { DivWithPrimaryColorVariable } from './div-with-primary-color-variable';
import {
	usePortalCampaignsQuery,
	usePortalMetadataQuery,
	usePortalCampaignsAggregatesQuery,
} from '@/lib/queries-portal';
import { useClient } from '@worldcoo/ui/hooks/use-client';
import { useEphemeralStore } from '@/lib/app-store';
import { isNotFound, useRouteContext } from '@tanstack/react-router';
import { PortalAnimateNumber } from './portal-animate-number';
import { usePortalDisplayContext } from './portal-display-context';
import { formatNumber } from '@worldcoo/ui/lib/utils';

export function Statistics() {
	const portalData = usePortalDisplayContext();
	if (portalData) {
		return (
			<StatisticsContent
				campaignsValue={formatNumber(portalData.locale, portalData.campaigns)}
				donationsValue={formatNumber(portalData.locale, portalData.donors)}
				collectedValue={
					<>
						<span>{formatNumber(portalData.locale, portalData.collected)}</span>
						<span>€</span>
					</>
				}
				hideCollected={portalData.hideCollected}
			/>
		);
	}

	return (
		<Suspense fallback={<StatisticsLoading />}>
			<SuspensedStatistics />
		</Suspense>
	);
}

function StatisticsContent(props: {
	campaignsValue: ReactNode;
	donationsValue: ReactNode;
	collectedValue: ReactNode;
	hideCollected: boolean;
}) {
	const { t } = useLingui();
	return (
		<DivWithPrimaryColorVariable className="bg-background shadow-4xl flex w-full justify-between gap-5 rounded-2xl p-4 sm:px-6 sm:py-5 lg:shadow-none">
			<div className="flex w-full flex-col font-medium">
				<HeartIcon className="text-primary mb-2 size-8" />
				<div className="text-lg whitespace-nowrap sm:text-2xl">{props.campaignsValue}</div>
				<span className="text-primary text-xs sm:text-base">{t`Campaigns`}</span>
			</div>

			<div className="flex w-full flex-col font-medium">
				<DonationsIcon className="text-primary mb-2" />
				<div className="text-lg whitespace-nowrap sm:text-2xl">{props.donationsValue}</div>
				<span className="text-primary text-xs sm:text-base">{t`Donations`}</span>
			</div>

			{!props.hideCollected && (
				<div className="flex w-full flex-col font-medium">
					<CollectedIcon className="text-primary mb-2" />
					<div className="flex gap-1.25 text-lg whitespace-nowrap sm:text-2xl">{props.collectedValue}</div>
					<span className="text-primary text-xs sm:text-base">{t`Collected`}</span>
				</div>
			)}
		</DivWithPrimaryColorVariable>
	);
}

function StatisticsLoading() {
	return (
		<StatisticsContent
			campaignsValue={<EmptyChar />}
			donationsValue={<EmptyChar />}
			collectedValue={<EmptyChar />}
			hideCollected={false}
		/>
	);
}

function SuspensedStatistics() {
	const metadata = usePortalMetadataQuery();
	const aggregates = usePortalCampaignsAggregatesQuery();
	const pagesLoaded = useEphemeralStore((store) => store.pagesLoaded);
	const isHomeLoaded = pagesLoaded.includes('home');

	// Hide component when metadata or aggregates are unavailable
	if (isNotFound(metadata)) return null;
	if (!aggregates || !('totalAggregate' in aggregates)) {
		return null;
	}

	return (
		<StatisticsContent
			campaignsValue={<Campaigns textEffectDisabled={isHomeLoaded} />}
			donationsValue={<Donations textEffectDisabled={isHomeLoaded} />}
			collectedValue={<Collected textEffectDisabled={isHomeLoaded} />}
			hideCollected={metadata.hideCollected}
		/>
	);
}

function Campaigns(props: { textEffectDisabled?: boolean }) {
	const { locale } = useRouteContext({ from: '__root__' });
	const data = usePortalCampaignsQuery();
	const isClient = useClient();

	if (isNotFound(data)) return null;

	const { campaignsCount } = data;
	const number = !props.textEffectDisabled && !isClient ? 0 : campaignsCount;

	return <PortalAnimateNumber locale={locale} value={number} />;
}

function Donations(props: { textEffectDisabled?: boolean }) {
	const { locale } = useRouteContext({ from: '__root__' });
	const aggregates = usePortalCampaignsAggregatesQuery();
	const isClient = useClient();
	const number =
		!props.textEffectDisabled && !isClient
			? 0
			: aggregates && 'totalAggregate' in aggregates
				? aggregates.totalAggregate.donors
				: 0;

	return <PortalAnimateNumber testId="donations" locale={locale} value={number} />;
}

function Collected(props: { textEffectDisabled?: boolean }) {
	const { locale } = useRouteContext({ from: '__root__' });
	const aggregates = usePortalCampaignsAggregatesQuery();
	const isClient = useClient();
	const number =
		!props.textEffectDisabled && !isClient
			? 0
			: aggregates && 'totalAggregate' in aggregates
				? aggregates.totalAggregate.collected
				: 0;

	return <PortalAnimateNumber testId="collected" locale={locale} value={number} suffix="€" />;
}
